import React, { useMemo, useCallback, useRef, useState } from "react";
import Icon1 from "@assets/icons/Icon";
import { usePlanningStore, CATEGORIES } from "@store/planningStore";
import { AppointmentCategory, Appointment } from "../../types/garage/planning";
import { snapToGrid, formatTime } from "@utils/timeGrid";
import OrdreModal from "@components/modals/OrdreModal";
import { findOrdreForPlate, buildOrdreFromAppointment, getOrdreById } from "@store/ordreStore";
import { ordresApi } from "@services/api/ordres";
import type { Ordre } from "types/ordre";

const HOURS_START = 8;
const HOURS_END = 20;
const HOUR_BLOCK_HEIGHT = 128;
const SNAP_PX = 16;
const SNAP_HOUR = 0.125;

const GRID_CATEGORIES: AppointmentCategory[] = [
  "rdv_divers",
  "location_rac",
  "vehicule_pret",
  "carrosserie",
  "reception_1",
  "reception_2",
];

function getCategoryConfig(id: AppointmentCategory) {
  return CATEGORIES.find((c) => c.id === id)!;
}

interface DragPreview {
  apptId: string;
  startHour: number;
  durationHours: number;
  category: AppointmentCategory;
}

const CLICK_THRESHOLD = 5; // px — if pointer moves less, treat as click

export function TimeGridWithAppointments() {
  const getFilteredAppointments = usePlanningStore(
    (s) => s.getFilteredAppointments,
  );
  const activeCategories = usePlanningStore((s) => s.activeCategories);
  const updateAppointment = usePlanningStore((s) => s.updateAppointment);
  const appointments = getFilteredAppointments();

  const [selectedOrdre, setSelectedOrdre] = useState<Ordre | null>(null);
  const [hoveredApptId, setHoveredApptId] = useState<string | null>(null);
  const clickStartRef = useRef<{ x: number; y: number } | null>(null);

  const visibleCategories = GRID_CATEGORIES.filter((c) =>
    activeCategories.includes(c),
  );
  const hours = Array.from(
    { length: HOURS_END - HOURS_START },
    (_, i) => HOURS_START + i,
  );

  const byCategory = useMemo(() => {
    const map: Record<string, Appointment[]> = {};
    for (const cat of GRID_CATEGORIES) map[cat] = [];
    for (const a of appointments) {
      if (map[a.category]) map[a.category].push(a);
    }
    return map;
  }, [appointments]);

  const dragRef = useRef<{
    apptId: string;
    mode: "move" | "resize";
    startY: number;
    origStartHour: number;
    origDuration: number;
    origCategory: AppointmentCategory;
  } | null>(null);
  const [preview, setPreview] = useState<DragPreview | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);

  const handlePointerDown = useCallback(
    (e: React.PointerEvent, appt: Appointment, mode: "move" | "resize") => {
      e.preventDefault();
      e.stopPropagation();
      (e.target as HTMLElement).setPointerCapture(e.pointerId);

      clickStartRef.current = { x: e.clientX, y: e.clientY };
      dragRef.current = {
        apptId: appt.id,
        mode,
        startY: e.clientY,
        origStartHour: appt.startHour,
        origDuration: appt.durationHours,
        origCategory: appt.category,
      };
      setPreview({
        apptId: appt.id,
        startHour: appt.startHour,
        durationHours: appt.durationHours,
        category: appt.category,
      });
    },
    [],
  );

  const handlePointerMove = useCallback((e: React.PointerEvent) => {
    const d = dragRef.current;
    if (!d) return;

    const deltaY = e.clientY - d.startY;
    const snapDelta = Math.round(deltaY / SNAP_PX);

    if (d.mode === "move") {
      const newStart = snapToGrid(
        d.origStartHour + snapDelta * SNAP_HOUR,
        SNAP_HOUR,
      );
      const clamped = Math.max(
        HOURS_START,
        Math.min(newStart, HOURS_END - d.origDuration),
      );

      let newCategory = d.origCategory;
      if (scrollRef.current) {
        const cols =
          scrollRef.current.querySelectorAll<HTMLElement>("[data-cat-col]");
        cols.forEach((el) => {
          const rect = el.getBoundingClientRect();
          if (e.clientX >= rect.left && e.clientX < rect.right) {
            newCategory = el.getAttribute(
              "data-cat-col",
            ) as AppointmentCategory;
          }
        });
      }

      setPreview({
        apptId: d.apptId,
        startHour: clamped,
        durationHours: d.origDuration,
        category: newCategory,
      });
    } else {
      const newDuration = snapToGrid(
        d.origDuration + snapDelta * SNAP_HOUR,
        SNAP_HOUR,
      );
      const clamped = Math.max(
        SNAP_HOUR,
        Math.min(newDuration, HOURS_END - d.origStartHour),
      );
      setPreview({
        apptId: d.apptId,
        startHour: d.origStartHour,
        durationHours: clamped,
        category: d.origCategory,
      });
    }
  }, []);

  const handlePointerUp = useCallback((e: React.PointerEvent) => {
    const d = dragRef.current;
    const p = preview;
    const start = clickStartRef.current;

    // Check if this was a click (no significant movement)
    const isClick = start &&
      Math.abs(e.clientX - start.x) < CLICK_THRESHOLD &&
      Math.abs(e.clientY - start.y) < CLICK_THRESHOLD &&
      d?.mode === "move";

    if (isClick && d) {
      const appt = appointments.find((a) => a.id === d.apptId);
      if (appt) {
        // Fetch fresh ordre from API so dragged times are reflected
        if (appt.ordre_id) {
          ordresApi.get(appt.ordre_id).then((ordre) => setSelectedOrdre(ordre)).catch(() => {
            setSelectedOrdre(findOrdreForPlate(appt.plate) || buildOrdreFromAppointment(appt));
          });
        } else {
          setSelectedOrdre(findOrdreForPlate(appt.plate) || buildOrdreFromAppointment(appt));
        }
      }
    } else if (d && p) {
      updateAppointment(d.apptId, {
        startHour: p.startHour,
        durationHours: p.durationHours,
        category: p.category,
      });
    }
    dragRef.current = null;
    clickStartRef.current = null;
    setPreview(null);
  }, [preview, updateAppointment, appointments]);

  const headerHeight = 28;
  const hourLabelWidth = 32;
  const minuteColWidth = 24;

  return (
    <div
      className="relative"
      style={{
        border: "1px solid #D9D9D9",
        borderRadius: 10,
        overflow: "hidden",
      }}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      onPointerLeave={handlePointerUp}
    >
      {/* Column headers */}
      <div className="flex flex-row" style={{ height: headerHeight }}>
        <div
          style={{
            width: hourLabelWidth + minuteColWidth + 1,
            borderRight: "1px solid #D9D9D9",
            borderBottom: "1px solid #D9D9D9",
          }}
        />
        {visibleCategories.map((catId) => {
          const cat = getCategoryConfig(catId);
          return (
            <div
              key={catId}
              className="flex-1 flex items-center justify-center text-sm font-medium whitespace-nowrap"
              style={{
                borderRight: "1px solid #D9D9D9",
                borderBottom: "1px solid #D9D9D9",
                background: "#FFFFFF",
                color: cat.color,
              }}
            >
              {cat.label === "Location" ? "Location RAC" : cat.label}
            </div>
          );
        })}
      </div>

      {/* Scrollable body */}
      <div ref={scrollRef} className="relative">
        <div className="flex flex-col">
          {hours.map((h) => (
            <div
              key={h}
              className="flex flex-row"
              style={{
                height: HOUR_BLOCK_HEIGHT,
                borderBottom: "1px solid #D9D9D9",
              }}
            >
              <div
                className="flex items-start"
                style={{
                  width: hourLabelWidth,
                  paddingLeft: 7,
                  paddingTop: 2,
                  fontFamily: "Poppins",
                  fontWeight: 500,
                  fontSize: 12,
                  color: "#A1A1A1",
                  flexShrink: 0,
                }}
              >
                {h}h
              </div>

              <div
                className="flex flex-col"
                style={{
                  width: minuteColWidth,
                  borderLeft: "1px solid #D9D9D9",
                  borderRight: "1px solid #D9D9D9",
                  flexShrink: 0,
                }}
              >
                {["00", "15", "30", "45"].map((m) => (
                  <div
                    key={m}
                    className="flex items-center justify-center"
                    style={{
                      height: 32,
                      borderBottom: "1px solid #D9D9D9",
                      fontFamily: "Poppins",
                      fontWeight: 500,
                      fontSize: 12,
                      color: "#A1A1A1",
                    }}
                  >
                    {m}
                  </div>
                ))}
              </div>

              {/* Category columns */}
              {visibleCategories.map((catId) => {
                const colAppts = (byCategory[catId] || []).filter(
                  (a) => a.startHour >= h && a.startHour < h + 1,
                );

                const ghostInCell =
                  preview &&
                  preview.category === catId &&
                  preview.startHour >= h &&
                  preview.startHour < h + 1
                    ? preview
                    : null;

                return (
                  <div
                    key={catId}
                    data-cat-col={catId}
                    className="flex-1 relative"
                    style={{ borderRight: "1px solid #D9D9D9" }}
                  >
                    {[0, 1, 2, 3].map((q) => (
                      <div
                        key={q}
                        style={{
                          height: 32,
                          display: "flex",
                          flexDirection: "column",
                        }}
                      >
                        <div
                          style={{
                            height: 16,
                            borderBottom: "1px solid #D9D9D9",
                          }}
                        />
                        <div
                          style={{
                            height: 16,
                            background: "#F4F4F4",
                            borderBottom: "1px solid #D9D9D9",
                          }}
                        />
                      </div>
                    ))}

                    {/* Ghost preview */}
                    {ghostInCell &&
                      (() => {
                        const config = getCategoryConfig(ghostInCell.category);
                        const topPx =
                          ((ghostInCell.startHour - h) / SNAP_HOUR) * SNAP_PX;
                        const heightPx =
                          (ghostInCell.durationHours / SNAP_HOUR) * SNAP_PX;
                        return (
                          <div
                            className="absolute left-0 right-0 flex flex-col items-center justify-center pointer-events-none"
                            style={{
                              top: topPx,
                              height: Math.max(heightPx, 20),
                              background: config.bgColor,
                              border: `2px dashed ${config.color}`,
                              borderRadius: 5,
                              margin: "0 2px",
                              zIndex: 40,
                              opacity: 0.7,
                            }}
                          >
                            <span
                              className="text-h4 font-medium"
                              style={{
                                color: config.color,
                                lineHeight: "100%",
                              }}
                            >
                              {formatTime(ghostInCell.startHour)} –{" "}
                              {formatTime(
                                ghostInCell.startHour +
                                  ghostInCell.durationHours,
                              )}
                            </span>
                          </div>
                        );
                      })()}

                    {/* Actual appointments */}
                    {colAppts.map((appt) => {
                      const config = getCategoryConfig(appt.category);
                      const topPx =
                        ((appt.startHour - h) / SNAP_HOUR) * SNAP_PX;
                      const heightPx =
                        (appt.durationHours / SNAP_HOUR) * SNAP_PX;
                      const isDragSource = preview?.apptId === appt.id;
                      const isHovered = hoveredApptId === appt.id;
                      return (
                        <div
                          key={appt.id}
                          className="absolute left-0 right-0 flex flex-col"
                          style={{
                            top: topPx,
                            height: Math.max(heightPx, 20),
                            background: config.bgColor,
                            border: `1px solid ${config.borderColor}`,
                            boxShadow: isHovered
                              ? "0px 4px 12px rgba(0, 0, 0, 0.25)"
                              : "0px 2px 4px rgba(0, 0, 0, 0.15)",
                            borderRadius: 5,
                            padding: "3px 4px",
                            overflow: "hidden",
                            zIndex: isDragSource ? 5 : isHovered ? 15 : 10,
                            margin: "0 2px",
                            cursor: "grab",
                            userSelect: "none",
                            opacity: isDragSource ? 0.35 : 1,
                            transition: isDragSource
                              ? "opacity 0.15s"
                              : "box-shadow 0.15s",
                          }}
                          onPointerDown={(e) =>
                            handlePointerDown(e, appt, "move")
                          }
                          onMouseEnter={() => setHoveredApptId(appt.id)}
                          onMouseLeave={() => setHoveredApptId(null)}
                        >
                          {/* Hover tooltip */}
                          {isHovered && !preview && (
                            <div
                              className="fixed z-50 bg-neutral-800 text-white rounded-lg px-3 py-2 pointer-events-none"
                              style={{ whiteSpace: "nowrap", minWidth: 140, transform: "translate(8px, 0)" }}
                              ref={(el) => {
                                if (!el) return;
                                const parent = el.parentElement;
                                if (!parent) return;
                                const rect = parent.getBoundingClientRect();
                                el.style.top = `${rect.top}px`;
                                el.style.left = `${rect.right}px`;
                              }}
                            >
                              <div className="text-h4 font-medium">{appt.clientName}</div>
                              {appt.plate && <div className="text-h5 text-neutral-300">{appt.plate}</div>}
                              <div className="text-h5 text-neutral-300">
                                {formatTime(appt.startHour)} — {formatTime(appt.startHour + appt.durationHours)}
                              </div>
                              <div className="text-h5 text-neutral-400 mt-1">Cliquer pour modifier</div>
                            </div>
                          )}
                          <div
                            className="flex flex-row flex-1"
                            style={{ gap: 5, minWidth: 0, overflow: "hidden" }}
                          >
                            <span
                              className="text-h4 font-medium"
                              style={{
                                lineHeight: "140%",
                                color: config.color,
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                                flex: 1,
                              }}
                            >
                              {appt.clientName}
                              {appt.plate ? ` ${appt.plate}` : ""}
                            </span>
                            <div
                              className="flex flex-row items-start"
                              style={{ flexShrink: 0 }}
                            >
                              {appt.hasCarIcon ? (
                                <Icon1
                                  name="car-reception"
                                  size={14}
                                  color={config.color}
                                />
                              ) : appt.hasMailIcon ? (
                                <Icon1
                                  name="car-restitution"
                                  size={14}
                                  color={config.color}
                                />
                              ) : null}
                            </div>
                          </div>
                          <div
                            className="absolute bottom-0 left-0 right-0 flex justify-center items-center"
                            style={{ height: 8, cursor: "ns-resize" }}
                            onPointerDown={(e) => {
                              e.stopPropagation();
                              handlePointerDown(e, appt, "resize");
                            }}
                          >
                            <div
                              style={{
                                width: 20,
                                height: 2,
                                borderRadius: 1,
                                background: config.color,
                                opacity: 0.4,
                              }}
                            />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      </div>
      {selectedOrdre && (
        <OrdreModal
          ordre={selectedOrdre}
          onClose={() => setSelectedOrdre(null)}
          onSave={() => setSelectedOrdre(null)}
        />
      )}
    </div>
  );
}
